import React, { Component } from "react";
import Button from "../base-components/button";
import classNames from "classnames";
import { Checkbox, Radio, RadioGroup } from "@blueprintjs/core";
import Modal from "shared/components/base-components/Modal";
import { sendAPIRequest } from "shared/utils/requests";

import AudioScrubber from "./audioScrubber";
import { Scrubber } from "react-scrubber";
import { soundManager } from "soundmanager2";
import Popup from "../base-components/popup";
import { FAQ_DOCUMENT_URL } from "../../utils";
import SnippetExamplePng from "../../assets/snippet-example.png";
import Select from "react-select";
import TranscriptEditor from "./TranscriptEditor";
import {
  EditorState,
  genKey,
  Modifier,
  SelectionState,
  convertToRaw,
  convertFromRaw,
} from "draft-js";
import lscache from "lscache";

const TRANSCRIPT_SOUND = "transcript_sound";
const ensureSoundManager = (() => {
  let hasLoaded = false;
  soundManager.onready(() => (hasLoaded = true));
  return (f) => {
    if (hasLoaded) {
      f();
    } else {
      soundManager.onready(f);
    }
  };
})();

function millisecondsToTimestamp(ms) {
  let numSeconds = Math.floor(ms / 1000);
  let numMinutes = Math.floor(numSeconds / 60);

  numSeconds %= 60;

  if (numSeconds < 10) {
    numSeconds = "0" + numSeconds;
  }

  return numMinutes + ":" + numSeconds;
}

// TODO: See if we can clean up this method -- defining the initial block
//  before the main loop and then pushing the final block after the main loop
//  seems messy.
function parseTranscriptSegments(segments, hasEvents, secondPass) {
  let parsedContent = {
    blocks: [],
    entityMap: {},
  };

  if (!segments) {
    return parsedContent;
  }

  let block = {
    text: "",
    type: segments[0].isCustomer ? "CustomerBubble" : "RepBubble",
    data: {
      eventQualityReasons: segments[0]["eventQualityReasons"],
      eventRedactionReasons: segments[0]["eventRedactionReasons"],
      // A call that doesn't have events hasn't been auto-transcribed or
      // manually transcribed, so it always needs revision. Second pass calls
      // should have all events subject to editing.
      needsRevision:
        secondPass ||
        !hasEvents ||
        segments[0]["eventQualityReasons"].length > 0,
    },
    entityRanges: [],
  };

  segments.forEach((segment) => {
    let contents = segment.content;

    if (block.type === "CustomerBubble" && !segment.isCustomer) {
      parsedContent.blocks.push(block);
      block = {
        text: "",
        type: "RepBubble",
        entityRanges: [],
      };
    } else if (block.type === "RepBubble" && segment.isCustomer) {
      parsedContent.blocks.push(block);
      block = {
        text: "",
        type: "CustomerBubble",
        entityRanges: [],
      };
    }

    block["data"] = {
      eventQualityReasons: segment["eventQualityReasons"],
      eventRedactionReasons: segment["eventRedactionReasons"],
      // A call that doesn't have events hasn't been auto-transcribed or
      // manually transcribed, so it always needs revision. Second pass calls
      // should have all events subject to editing.
      needsRevision:
        secondPass || !hasEvents || segment["eventQualityReasons"].length > 0,
    };

    contents.forEach((content) => {
      if (content.type === "text") {
        block.text = `${block.text} ${content.content}`;
      } else if (content.type === "play") {
        let ts = millisecondsToTimestamp(content.time);
        let entityKey = genKey();

        block.text = `${block.text} ${ts}`;

        block.entityRanges.push({
          offset: block.text.length - ts.length,
          length: ts.length,
          key: entityKey,
        });

        parsedContent.entityMap[entityKey] = {
          type: "TOKEN",
          mutability: "IMMUTABLE",
          data: {
            playing: false,
            timestampMS: content.time,
          },
        };
      }
    });
  });

  parsedContent.blocks.push(block);

  return parsedContent;
}

// a quadratic for the input curve of audio playback speed
const userSpeedToAudioSpeed = (input) => {
  return input * input + input * 0.5 + 0.5;
};

// inverse of above function
const audioSpeedToUserSpeed = (input) => {
  return -0.25 + 0.25 * Math.sqrt(16 * input - 7);
};

class Transcript extends Component {
  boundHandleKeyDown = this.handleKeyDown.bind(this);
  boundHandleScroll = this.handleScroll.bind(this);

  constructor(props) {
    super(props);

    const is_veteran_transcriber =
      !this.props.currentContractor.early_transcriber;

    this.initialSegments = parseTranscriptSegments(
      this.props.segments,
      this.props.transcription.has_events,
      this.props.secondPass
    );

    // Remove old cached content states. NOTE: If we start using lscache in other modules, move this somewhere central.
    lscache.flushExpired();

    let persistedContentStateID =
      "contentState-" + this.props.transcription.display_id;
    let contentStateSerialized = lscache.get(persistedContentStateID);

    this.state = {
      timestamp: 0.0,
      isPlaying: false,
      soundUpdateInterval: null,
      manualTimestamp: false,
      playbackSpeed: 1.0,
      currentModal: null,
      windowScrollY: window.scrollY,
      windowScrollDirection: -1, // -1 = down, 1 = up
      playMode: "snippet",
      isLooping: false,
      isSubmitting: false,
      persistedContentStateID: persistedContentStateID,
      submitValidationCheckboxes: [
        {
          content: "Assigned the proper customer and rep labels",
          isChecked: is_veteran_transcriber,
        },
        {
          content: "Accurately edited the transcript to reflect the audio",
          isChecked: is_veteran_transcriber,
        },
        {
          content:
            "Checked for proper spelling, grammar, punctuation and capitalization",
          isChecked: is_veteran_transcriber,
        },
        {
          content: (
            <span>
              Transcribed according to the{" "}
              <a
                href={FAQ_DOCUMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
            </span>
          ),
          isChecked: is_veteran_transcriber,
        },
      ],
      playingSegmentTime: null,
      manualSegment: false,
      modalChain: [],
      removedSegments: false,
      hideEventQualityReasons: false,
      editorState: EditorState.createEmpty(),
      cachedContentState:
        contentStateSerialized !== null
          ? convertFromRaw(JSON.parse(contentStateSerialized))
          : null,
    };

    this.toggleHideEventQualityReasons =
      this.toggleHideEventQualityReasons.bind(this);
  }

  getCurrentSubSegmentTime() {
    const { segments } = this.props;
    const { timestamp } = this.state;
    const msTimestamp = timestamp * 1000.0;
    let nextTime = null;
    for (let i = segments.length - 1; i >= 0; --i) {
      for (let j = segments[i].content.length - 1; j >= 0; --j) {
        const item = segments[i].content[j];
        if (item.type !== "play") {
          continue;
        }
        if (
          item.time <= msTimestamp &&
          (nextTime == null || nextTime > msTimestamp)
        ) {
          return item.time;
        }
        nextTime = item.time;
      }
    }
    return null;
  }

  handleKeyDown(e) {
    const { transcription } = this.props;

    const { timestamp, isPlaying, playbackSpeed } = this.state;

    if (!e.ctrlKey && !e.altKey && !e.shiftKey) {
      const hotkeys = {
        F7: () =>
          this.setState({
            timestamp: Math.max(0.0, timestamp - 5.0),
            manualTimestamp: true,
          }),
        Escape: () => this.setState({ isPlaying: !isPlaying }),
        F9: () =>
          this.setState({
            timestamp: Math.min(timestamp + 5.0, transcription.call_length_sec),
            manualTimestamp: true,
          }),
        F2: () => {
          const targetUserSpeed = Math.max(
            audioSpeedToUserSpeed(playbackSpeed) - 0.1,
            0.0
          );
          this.setState({
            playbackSpeed: userSpeedToAudioSpeed(targetUserSpeed),
          });
        },
        F3: () => {
          const targetUserSpeed = Math.min(
            audioSpeedToUserSpeed(playbackSpeed) + 0.1,
            1.0
          );
          this.setState({
            playbackSpeed: userSpeedToAudioSpeed(targetUserSpeed),
          });
        },
      };

      if (hotkeys[e.key]) {
        hotkeys[e.key]();
        e.preventDefault();
      }
    }
  }

  handleScroll() {
    const { windowScrollY } = this.state;

    this.setState({
      windowScrollY: window.scrollY,
      windowScrollDirection: window.scrollY - windowScrollY < 0 ? 1 : -1,
    });
  }

  componentDidMount() {
    const {
      transcription,
      missingTranscriptAppearances,
      eventLevelPopupAppearances,
      segments,
      segmentsRemoved,
    } = this.props;
    if (transcription.display_id === segmentsRemoved) {
      this.setState({ removedSegments: true, playMode: "continuous" });
    }

    ensureSoundManager(() => {
      soundManager.createSound({
        id: TRANSCRIPT_SOUND,
        url: transcription.audio_url,
        onfinish: () => {
          let returnToTime = 0.0;
          if (this.state.playMode === "snippet") {
            returnToTime = this.state.playingSegmentTime / 1000.0;
          }
          this.setState(
            {
              isPlaying: false,
              timestamp: returnToTime,
              manualTimestamp: true,
            },
            () => this.setState({ isPlaying: this.state.isLooping })
          );
        },
        autoLoad: true,
      });
    });
    let modalChain = [];
    if (
      missingTranscriptAppearances == null ||
      (missingTranscriptAppearances.length < 3 &&
        !missingTranscriptAppearances.includes(transcription.display_id))
    ) {
      if (
        segments.filter(
          (segment) =>
            segment.content != null &&
            segment.content.filter((item) => item.type === "timestamp").length >
              0
        ).length > 0
      ) {
        modalChain.push("missing");
      }
    }
    if (
      eventLevelPopupAppearances == null ||
      (eventLevelPopupAppearances.length < 3 &&
        !eventLevelPopupAppearances.includes(transcription.display_id))
    ) {
      modalChain.push("event-level-editing");
    }
    this.setState({ modalChain });
    window.addEventListener("keydown", this.boundHandleKeyDown);
    window.addEventListener("scroll", this.boundHandleScroll);
    window.addEventListener(
      "beforeunload",
      function (e) {
        const { persistedContentStateID, editorState } = this.state;

        const contentState = editorState.getCurrentContent();
        const contentStateSerialized = JSON.stringify(
          convertToRaw(contentState)
        );

        // `lscache.set` args are key, value, lifetime in minutes. 60 * 24 sets expiration to 1 day.
        lscache.set(persistedContentStateID, contentStateSerialized, 60 * 24);

        // Per MDN, Chrome requires returnValue to be set:
        // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
        e.preventDefault();
        e.returnValue = "";
      }.bind(this)
    );
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.boundHandleKeyDown);
    window.removeEventListener("scroll", this.boundHandleScroll);
  }

  componentDidUpdate(prevProps, prevState) {
    let sound = soundManager.getSoundById(TRANSCRIPT_SOUND);
    if (!prevState.isPlaying && this.state.isPlaying) {
      sound.play();
      this.setState({
        soundUpdateInterval: setInterval(() => {
          this.setState({
            timestamp: sound.position / 1000.0,
            manualTimestamp: false,
          });
        }, 500),
      });
    } else if (prevState.isPlaying && !this.state.isPlaying) {
      sound.pause();
      let { soundUpdateInterval } = this.state;
      clearInterval(soundUpdateInterval);
      this.setState({ soundUpdateInterval: null });
    }
    if (prevState.timestamp !== this.state.timestamp) {
      if (this.state.manualTimestamp) {
        sound.setPosition(this.state.timestamp * 1000.0);
      }
      const currentTime = this.getCurrentSubSegmentTime();
      if (
        currentTime != null &&
        currentTime !== this.state.playingSegmentTime
      ) {
        this.setState({
          playingSegmentTime: currentTime,
          manualSegment: this.state.manualTimestamp,
        });
      }
    }
    if (prevState.playbackSpeed !== this.state.playbackSpeed) {
      sound.setPlaybackRate(this.state.playbackSpeed);
    }
    if (prevState.playingSegmentTime !== this.state.playingSegmentTime) {
      if (this.state.manualSegment) {
        this.setState({ manualSegment: false });
      } else {
        if (this.state.playMode === "snippet") {
          const previousTime = prevState.playingSegmentTime;
          const targetTimestamp = previousTime / 1000.0;
          setTimeout(() => {
            // clear event queue
            if (this.state.isLooping) {
              this.setState({
                timestamp: targetTimestamp,
                manualTimestamp: true,
              });
            } else {
              this.setState({
                isPlaying: false,
                timestamp: targetTimestamp,
                manualTimestamp: true,
              });
            }
          }, 0);
        }
      }
    }
    if (
      prevState.currentModal != null &&
      this.state.currentModal != null &&
      this.state.message != null
    ) {
      this.openModal(null);
    }
    if (
      prevState.messageShouldRedirect !== this.state.messageShouldRedirect &&
      this.state.messageShouldRedirect
    ) {
      setTimeout(() => {
        if (this.state.messageShouldRedirect) {
          window.location.href = "/calls";
        }
      }, 2000);
    }
    if (this.state.currentModal == null && this.state.modalChain.length >= 1) {
      this.setState({
        modalChain: this.state.modalChain.slice(1),
        currentModal: this.state.modalChain[0],
      });
    }
  }

  changeAudioSpeed = () => {
    const { audioSpeed } = this.state;
    const newSpeed = (audioSpeed >= 2 ? 2 : audioSpeed) + 0.25;

    const updateAudio = () => (this.$audio.playbackRate = newSpeed);
    this.setState({ audioSpeed: newSpeed }, updateAudio);
  };

  openModal = (name) => {
    const { currentModal } = this.state;

    this.setState({ currentModal: currentModal === name ? null : name });
  };

  async flagCall(reason) {
    const { transcription } = this.props;
    const response = await sendAPIRequest(
      `/transcriptions/${transcription.display_id}/new_release`,
      "POST",
      { flag_code: reason }
    );
    if (response.data.status === "error") {
      this.setState({
        message: response.data.message,
        messageType: "error",
        messageShouldRedirect: !!response.data.redirect,
      });
    } else if (response.data.status === "success") {
      // we ignore redirect here
      window.location.href = "/calls";
    }
  }

  handleStandardResponse(response) {
    if (response.data.status === "error") {
      this.setState({
        message: response.data.message,
        messageType: "error",
        messageShouldRedirect: !!response.data.redirect,
      });
    } else if (response.data.status === "success") {
      this.setState({
        message: response.data.message,
        messageType: "success",
        messageShouldRedirect: !!response.data.redirect,
      });
    }

    this.setState({ isSubmitting: false });
  }

  async reserveHours(hours) {
    const { transcription } = this.props;
    const response = await sendAPIRequest(
      `/transcriptions/${transcription.display_id}/new_reserve`,
      "POST",
      { hours: hours }
    );
    this.handleStandardResponse(response);
  }

  removeSnippetButtons() {
    const { editorState } = this.state;
    let contentState = editorState.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    blocks.forEach((contentBlock) => {
      let offset = 0;

      contentBlock.findEntityRanges(
        (character) => {
          const entityKey = character.getEntity();
          if (entityKey === null) {
            return false;
          }
          return (
            contentState.getEntity(entityKey).getMutability() === "IMMUTABLE"
          );
        },
        (start, end) => {
          let selectionState = SelectionState.createEmpty(
            contentBlock.getKey()
          );
          selectionState = selectionState.merge({
            anchorKey: contentBlock.getKey(),
            anchorOffset: start - offset,
            focusKey: contentBlock.getKey(),
            focusOffset: end - offset,
          });

          offset += end - start;

          contentState = Modifier.removeRange(
            contentState,
            selectionState,
            "forward"
          );
        }
      );

      let selectionState = SelectionState.createEmpty(contentBlock.getKey());
      selectionState = selectionState.merge({
        anchorKey: contentBlock.getKey(),
        anchorOffset: 0,
        focusKey: contentBlock.getKey(),
        focusOffset: contentBlock.getText().length,
      });

      let text = contentState.getBlockForKey(contentBlock.getKey()).getText();
      text = text.replace(/ +/g, " ");
      text = text.replace(/[\n]+/g, " ");
      text = text.trim();
      contentState = Modifier.replaceText(contentState, selectionState, text);
    });

    this.setState({ editorState: EditorState.push(editorState, contentState) });
  }

  toggleHideEventQualityReasons() {
    this.setState({
      hideEventQualityReasons: !this.state.hideEventQualityReasons,
    });
  }

  async submitTranscript() {
    if (this.state.isSubmitting) {
      window.alert("Submit is in progress");
      return null;
    }

    // This is a hack to reduce the frequency of edit reason text getting mixed
    // into the edited event text, a bug that appears to mostly affect mobile
    // but might also crop up in desktop environments.
    if (this.state.hideEventQualityReasons) {
      this.toggleHideEventQualityReasons();
    }

    this.setState({ isSubmitting: true });

    const { editorState } = this.state;

    const contentState = editorState.getCurrentContent();
    const blocks = contentState.getBlocksAsArray();

    let parsedTranscript = blocks.map((contentBlock) => {
      const textWithSnippets = contentBlock.getText();
      let textWithoutSnippets = "";
      let lastEnd = 0;

      contentBlock.findEntityRanges(
        (character) => {
          const entityKey = character.getEntity();
          if (entityKey === null) {
            return false;
          }
          return (
            contentState.getEntity(entityKey).getMutability() === "IMMUTABLE"
          );
        },
        (start, end) => {
          textWithoutSnippets += textWithSnippets.slice(lastEnd, start);
          lastEnd = end;
        }
      );

      textWithoutSnippets += textWithSnippets.slice(
        lastEnd,
        textWithSnippets.length
      );

      textWithoutSnippets = textWithoutSnippets.replace(/ +/g, " ");
      textWithoutSnippets = textWithoutSnippets.replace(/[\n]+/g, " ");
      textWithoutSnippets = textWithoutSnippets.trim();

      let isCustomer = null;
      if (contentBlock.getType() === "CustomerBubble") {
        isCustomer = true;
      } else if (contentBlock.getType() === "RepBubble") {
        isCustomer = false;
      }

      return {
        content: textWithoutSnippets,
        isCustomer: isCustomer,
      };
    });

    const { submitURL, transcription } = this.props;
    const response = await sendAPIRequest(submitURL(transcription), "POST", {
      segments: parsedTranscript,
    });
    this.handleStandardResponse(response);
  }

  renderModalFaq = () => {
    return (
      <Modal
        open
        header="FAQ"
        content={
          <div className="transcript-modal-content">
            <div>
              Listen to the audio and edit the transcription so they match. Hit
              enter when the speaker changes and make sure to correctly label
              the speakers.
            </div>
            <div>
              Please use our FAQ which contains all our style guidelines and
              rules for transcribing in QA World:
            </div>
            <div>
              <a
                href={FAQ_DOCUMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
            </div>
          </div>
        }
        footer={<Button onClick={() => this.openModal(null)} text="Close" />}
      />
    );
  };

  renderModalFlag = () => {
    const { currentContractor, transcription } = this.props;
    const { selectedFlagReason } = this.state;

    return (
      <Modal
        open
        header="Flag this call?"
        content={
          <div className="transcript-modal-content">
            <div>
              You can only flag 5 calls a day. You have{" "}
              {currentContractor.remaining_flags} flags remaining.
            </div>
            <div>
              If 5 other people also flag this call you will receive your token
              back. If you transcribe a call that should be flagged (please
              review the section in the FAQ), you will receive rating of 1 and
              will not be paid.
            </div>
            <div>
              If you abuse the flagging system and use it instead of the release
              system for releasing calls on personal preference, you will be
              removed from the QA World Platform
            </div>
            <div>
              <Select
                placeholder="Choose reason for flagging"
                options={transcription.flag_reasons.filter(
                  (reason) => reason.value < 300 || reason.value >= 400
                )}
                value={selectedFlagReason}
                onChange={(option) =>
                  this.setState({ selectedFlagReason: option })
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-flag"
              onClick={() =>
                selectedFlagReason && this.flagCall(selectedFlagReason.value)
              }
              text={"Flag Call"}
            />
            <Button
              className="transcript-button"
              onClick={() => this.openModal(null)}
              text="Cancel"
            />
          </div>
        }
      />
    );
  };

  renderModalRelease = () => {
    const { currentContractor, transcription } = this.props;
    const { selectedReleaseReason } = this.state;

    return (
      <Modal
        open
        header="Release this call?"
        content={
          <div className="transcript-modal-content">
            <div>
              You can only release 5 calls a day. You have{" "}
              {currentContractor.remaining_releases} releases remaining.
            </div>
            <div>You will not earn any money for a released call.</div>
            <div>
              <Select
                placeholder="Choose reason for releasing"
                options={transcription.flag_reasons.filter(
                  (reason) => reason.value >= 300 && reason.value < 400
                )}
                value={selectedReleaseReason}
                onChange={(option) =>
                  this.setState({ selectedReleaseReason: option })
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-dark-button"
              onClick={() =>
                selectedReleaseReason &&
                this.flagCall(selectedReleaseReason.value)
              }
              text={"Release Call"}
            />
            <Button
              className="transcript-button"
              onClick={() => this.openModal(null)}
              text="Cancel"
            />
          </div>
        }
      />
    );
  };

  renderModalInactivity = () => {
    const { currentlyClaimedCalls, transcription } = this.props;
    const { selectedInactivityTime } = this.state;

    const claimedCall = currentlyClaimedCalls.filter(
      (call) => call.id === transcription.display_id
    );
    if (claimedCall.length !== 1) {
      return null;
    }

    const secondsLeft =
      ((Date.parse(claimedCall[0].expires_at) - Date.now()) / 1000) | 0;
    const hoursLeft = Math.ceil(secondsLeft / 3600.0);

    let options = [];
    for (let i = 1; i < hoursLeft; ++i) {
      options.push({ label: `${i} hour${i > 1 ? "s" : ""}`, value: i });
    }

    return (
      <Modal
        open
        header="Prevent Inactivity Timeout"
        content={
          <div className="transcript-modal-content">
            <div>
              This will not extend your time remaining, but will prevent
              expirations due to inactivity.
            </div>
            <div>
              <Select
                placeholder="Choose length of time"
                options={options}
                value={selectedInactivityTime}
                onChange={(option) =>
                  this.setState({ selectedInactivityTime: option })
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-dark-button"
              onClick={() =>
                selectedInactivityTime &&
                this.reserveHours(selectedInactivityTime.value)
              }
              text={"Prevent Inactivity Timeout"}
            />
            <Button
              className="transcript-button"
              onClick={() => this.openModal(null)}
              text="Cancel"
            />
          </div>
        }
      />
    );
  };

  renderRemoveSnippets = () => {
    return (
      <Modal
        open
        header="Are you sure you want to remove all snippets?"
        content={
          <div className="transcript-modal-content">
            <div>
              If you choose to remove snippet play buttons you will only see the
              automatic transcript. You will not be able to navigate within the
              transcript based on snippets nor loop within a snippet section.
            </div>
            <div>
              Immediately after deleting you can press CTRL+Z (Win) or CMD+Z
              (Mac) in the editor to recover the snippet buttons.
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-dark-button"
              onClick={() => (
                this.setState({
                  removedSegments: false,
                  playMode: "continuous",
                }),
                this.removeSnippetButtons(),
                this.openModal(null)
              )}
              text={"Remove Snippets"}
            />
            <Button
              className="transcript-button"
              onClick={() => this.openModal(null)}
              text="Keep Snippets"
            />
          </div>
        }
      />
    );
  };

  renderMissingModal = () => {
    const { updateMissingTranscriptAppearance, transcription } = this.props;

    return (
      <Modal
        open
        className="transcript-popup-missing"
        header={`Welcome to QA World${"'"}s New Transcription Page!`}
        content={
          <div className="transcript-modal-content transcript-popup-missing-content">
            <div>
              Listen to the audio and correct the auto-transcription so it
              accurately reflects the call. Sometimes, we don{"'"}t have
              auto-transcription for a certain section. In the transcript that
              looks like this:
            </div>
            <div>
              <span className="segment-text-timestamp">
                0:10<span className="segment-text-timestamp-seperator">-</span>
                0:30
              </span>
            </div>
            <div>
              This indicates a section of audio where we don{"'"}t have an
              auto-transcript. Delete these sections and replace them with your
              own transcription.
            </div>
          </div>
        }
        footer={
          <Button
            className="transcript-dark-button"
            onClick={() => {
              updateMissingTranscriptAppearance(transcription.display_id);
              this.openModal(null);
            }}
            text={"Continue"}
          />
        }
      />
    );
  };

  renderEventLevelEditingModal = () => {
    const { updateEventLevelAppearance, transcription } = this.props;

    return (
      <Modal
        open
        className="transcript-popup-event-level-editing"
        header={
          <React.Fragment>
            A new approach to editing snippets is here!
          </React.Fragment>
        }
        content={
          <div className="transcript-modal-content transcript-popup-missing-content">
            <div>
              This call has {(transcription.call_length_sec / 60).toFixed(2)}
              {""} total minutes of audio. Of those total minutes, only {""}
              {(transcription.edit_events_length_sec / 60).toFixed(2)} minutes
              require editing. Sections that do not require editing are grayed
              out and are not editable. Sections that require editing are in
              standard colors with a yellow bubble alongside listing the edit
              reasons.
              <br />
              <br />
              Call Info:
            </div>
            <div>
              Audio Length: {(transcription.call_length_sec / 60).toFixed(2)}{" "}
              minutes
              <br />
              Audio to Transcribe:{" "}
              {(transcription.edit_events_length_sec / 60).toFixed(2)} minutes
              <br />
              Per Minute Rate: $
              {transcription.payment_rates
                .map((rate) => rate.toFixed(2))
                .join("/")}
              <br />
              Estimated Earning: $
              {transcription.estimated_earnings
                .map((rate) => rate.toFixed(2))
                .join("/")}
            </div>
            <div>
              <img alt="Screenshot of new snippet-level editing" src={SnippetExamplePng}/>
            </div>
          </div>
        }
        footer={
          <Button
            className="transcript-dark-button"
            onClick={() => {
              updateEventLevelAppearance(transcription.display_id);
              this.openModal(null);
            }}
            text={"Start Editing"}
          />
        }
      />
    );
  };

  renderGlossaryPopup = () => {
    const { transcription } = this.props;

    return (
      <Popup
        className="transcript-popup-glossary"
        header="Glossary"
        closePopup={() => this.openModal(null)}
        content={
          <div className="transcript-popup-glossary-content">
            {transcription.glossary.map((term, i) => (
              <div key={i}>{term}</div>
            ))}
          </div>
        }
      />
    );
  };

  renderMessage() {
    const { message, messageType, messageShouldRedirect } = this.state;

    const messageTypeHeaders = {
      error: "Error",
      success: "Success",
    };
    const headerClasses = classNames({
      "transcript-modal-error-header": messageType === "error",
      "transcript-modal-success-header": messageType === "success",
    });
    const messageTypeHeader = messageTypeHeaders[messageType] || "Unknown";

    return (
      <Modal
        open
        header={<span className={headerClasses}>{messageTypeHeader}</span>}
        content={
          <div className="transcript-modal-content">
            <div>{message}</div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-button"
              onClick={() =>
                messageShouldRedirect
                  ? (window.location.href = "/calls")
                  : this.setState({ message: null })
              }
              text="Continue"
            />
          </div>
        }
      />
    );
  }

  renderShortcutsPopup = () => {
    const hotkeys = {
      "Play/pause audio": "ESC",
      "Fast forward 5s": "F9",
      "Rewind 5s": "F7",
      "Increase playback speed": "F3",
      "Decrease playback speed": "F2",
      "Next segment": "CTRL + ↑",
      "Previous segment": "CTRL + ↓",
    };

    const hotkeyDivs = Object.entries(hotkeys).map(
      ([description, hotkey], i) => (
        <div key={i} className="transcript-popup-shortcuts-row">
          <div className="transcript-popup-shortcuts-label">{description}</div>
          <div className="transcript-popup-shortcuts-keys">
            {hotkey
              .split(" + ")
              .map((key, j) => (
                <div
                  className="transcript-popup-shortcuts-key"
                  key={`k-${i}-${j}`}
                >
                  {key}
                </div>
              ))
              .reduce((result, item) => (
                <React.Fragment>
                  {result} + {item}
                </React.Fragment>
              ))}
          </div>
        </div>
      )
    );

    return (
      <Popup
        className="transcript-popup-shortcuts"
        closePopup={() => this.openModal(null)}
        content={
          <div className="transcript-popup-shortcuts-content">{hotkeyDivs}</div>
        }
      />
    );
  };

  renderLowQualityModal = () => {
    return (
      <Modal
        open
        header={
          <span>
            <span className="transcript-modal-icon">
              <svg
                width="23"
                height="21"
                viewBox="0 0 23 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.73802 1.96274L1.26802 16.1027C1.09339 16.4052 1.00099 16.748 1.00001 17.0973C0.99903 17.4465 1.08951 17.7899 1.26245 18.0933C1.43538 18.3967 1.68474 18.6495 1.98573 18.8266C2.28671 19.0037 2.62882 19.0989 2.97802 19.1027H19.918C20.2672 19.0989 20.6093 19.0037 20.9103 18.8266C21.2113 18.6495 21.4607 18.3967 21.6336 18.0933C21.8065 17.7899 21.897 17.4465 21.896 17.0973C21.8951 16.748 21.8027 16.4052 21.628 16.1027L13.158 1.96274C12.9797 1.66885 12.7287 1.42586 12.4292 1.25723C12.1297 1.08859 11.7918 1 11.448 1C11.1043 1 10.7663 1.08859 10.4668 1.25723C10.1673 1.42586 9.91629 1.66885 9.73802 1.96274V1.96274Z"
                  stroke="#F23D60"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.448 7.10278V11.1028"
                  stroke="#F23D60"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.448 14.1028V15.1028"
                  stroke="#F23D60"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            Transcription Quality Low
          </span>
        }
        content={
          <div className="transcript-modal-content">
            <div>
              Make sure you have edited your transcript enough and assigned the
              proper customer and rep labels.
            </div>
            <div>
              <span className="transcript-modal-bold">
                If you submit this transcript and it is rated a 1 or 2, you will
                not be paid.
              </span>{" "}
              If you consistently engage in this behavior you will be removed
              from the site.
            </div>
            <div>
              We strongly recommend you continue transcribing and check for
              errors. Make sure to read the FAQ if you{"'"}re having trouble.
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-dark-button"
              onClick={() => this.openModal(null)}
              text={"Continue Transcribing"}
            />
            <Button
              className="transcript-button"
              onClick={() => this.openModal("submit")}
              text="Submit Anyway"
            />
          </div>
        }
      />
    );
  };

  renderHelpModal = () => {
    return (
      <Modal
        header="Need help with transcribing?"
        content={
          <div className="transcript-modal-content">
            <div>
              If you{"'"}re unsure what to do please first check the{" "}
              <a
                href={FAQ_DOCUMENT_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
              .
            </div>
            <div>
              If you can{"'"}t find your answer there please go to the{" "}
              <a
                href="https://app.slack.com/client/T6RP02VJM/C6TBXQ879"
                className="transcript-modal-bold"
                target="_blank"
                rel="noopener noreferrer"
              >
                #query-desk
              </a>{" "}
              slack channel and ask for help there.
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-button"
              onClick={() => this.openModal(null)}
              text="Done"
            />
          </div>
        }
      />
    );
  };

  isSubmitCheckboxesValidated = () => {
    const { submitValidationCheckboxes } = this.state;
    for (let i = 0; i < submitValidationCheckboxes.length; i++) {
      if (!submitValidationCheckboxes[i].isChecked) {
        return false;
      }
    }
    return true;
  };

  renderModalSubmitTranscript = () => {
    const { submitValidationCheckboxes } = this.state;
    const is_veteran_transcriber =
      !this.props.currentContractor.early_transcriber;

    return (
      <Modal
        open
        header="Are you sure you are ready to submit?"
        content={
          <div className="transcript-modal-content">
            <div>
              {is_veteran_transcriber
                ? "Double check"
                : "Please check the boxes to confirm"}{" "}
              that you have:
            </div>
            {submitValidationCheckboxes.map(
              (checkbox_data, checkbox_data_i) => {
                return (
                  <div key={checkbox_data_i}>
                    <Checkbox
                      label={checkbox_data.content}
                      checked={checkbox_data.isChecked}
                      onChange={() => {
                        const { submitValidationCheckboxes } = this.state;
                        submitValidationCheckboxes[checkbox_data_i].isChecked =
                          !submitValidationCheckboxes[checkbox_data_i]
                            .isChecked;
                        this.setState({ submitValidationCheckboxes });
                      }}
                    />
                  </div>
                );
              }
            )}
            <div>
              If you have not edited the transcript well enough, you will not be
              paid for your work.
            </div>
          </div>
        }
        footer={
          <div className="transcript-modal-buttons">
            <Button
              className="transcript-submit"
              onClick={() => {
                if (this.isSubmitCheckboxesValidated()) {
                  this.submitTranscript();
                }
              }}
              text="Submit Transcript"
            >
              {this.state.isSubmitting && (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="sr-only">Loading...</span>
                </>
              )}
            </Button>
            <Button
              className="transcript-button"
              onClick={() => this.openModal(null)}
              text="Continue Transcribing"
            />
          </div>
        }
      />
    );
  };

  modals = {
    faq: this.renderModalFaq,
    flag: this.renderModalFlag,
    release: this.renderModalRelease,
    inactivity: this.renderModalInactivity,
    "remove-snippets": this.renderRemoveSnippets,
    glossary: this.renderGlossaryPopup,
    submit: this.renderModalSubmitTranscript,
    shortcuts: this.renderShortcutsPopup,
    "low-quality": this.renderLowQualityModal,
    help: this.renderHelpModal,
    missing: this.renderMissingModal,
    "event-level-editing": this.renderEventLevelEditingModal,
  };

  render() {
    const { secondPass, transcription, segments } = this.props;
    const {
      timestamp,
      isPlaying,
      playbackSpeed,
      currentModal,
      message,
      windowScrollY,
      windowScrollDirection,
      playMode,
      isLooping,
      removedSegments,
    } = this.state;

    let targetControlLevel = windowScrollY;
    if (document.body.scrollHeight - targetControlLevel < 850) {
      targetControlLevel = document.body.scrollHeight - 850;
    } else if (windowScrollDirection === -1) {
      const overflow = Math.max(0, 850 - window.innerHeight);
      targetControlLevel = windowScrollY - overflow;
    }

    const currentControlStyle = {
      top: targetControlLevel,
    };

    const handleEditorStateChange = (newEditorState) => {
      let oldEditorState = this.state.editorState;

      const oldContentState = oldEditorState.getCurrentContent();
      const oldSelectionState = oldEditorState.getSelection();
      const oldAnchorKey = oldSelectionState.getAnchorKey();
      const oldCurrentBlock = oldContentState.getBlockForKey(oldAnchorKey);
      const oldMetadata = Object.fromEntries(oldCurrentBlock.getData());
      const oldBlocks = oldContentState.getBlocksAsArray();

      const changeType = newEditorState.getLastChangeType();

      const newContentState = newEditorState.getCurrentContent();
      const newSelectionState = newEditorState.getSelection();
      const newAnchorKey = newSelectionState.getAnchorKey();
      const newCurrentBlock = newContentState.getBlockForKey(newAnchorKey);
      const newMetadata = Object.fromEntries(newCurrentBlock.getData());
      const newBlocks = newContentState.getBlocksAsArray();

      let allowChange = true;

      // When the editor is first spinning up, oldMetadata is empty.
      if (Object.keys(oldMetadata).length > 0) {
        // Prevent dragging of text from uneditable blocks into editable blocks.
        if (
          !oldMetadata.needsRevision &&
          ["insert-fragment"].includes(changeType)
        ) {
          allowChange = false;
          // Prevent the change of a block's needsRevision value.
        } else if (
          oldCurrentBlock.getKey() === newCurrentBlock.getKey() &&
          oldMetadata.needsRevision !== newMetadata.needsRevision
        ) {
          allowChange = false;
          // Prevent merging of a block with uneditable blocks on either side.
        } else if (oldBlocks.length > newBlocks.length) {
          // The old anchor key will be the same as the new anchor key when
          // oldNextBlock has been merged with oldCurrentBlock.
          if (oldAnchorKey === newAnchorKey) {
            const oldNextBlock = oldContentState.getBlockAfter(oldAnchorKey);
            if (oldNextBlock) {
              const metadata = Object.fromEntries(oldNextBlock.getData());
              if (!metadata.needsRevision) {
                allowChange = false;
              }
            }
            // The anchor keys will be different when oldCurrentBlock has been
            // merged with oldPreviousBlock.
          } else {
            const oldPreviousBlock =
              oldContentState.getBlockBefore(oldAnchorKey);
            if (oldPreviousBlock) {
              const metadata = Object.fromEntries(oldPreviousBlock.getData());
              if (!metadata.needsRevision) {
                allowChange = false;
              }
            }
          }
        }
      }

      if (allowChange) {
        this.setState({ editorState: newEditorState });
      } else {
        // If we're not allowing the change, we need to overwrite the current
        // EditorState and trigger a refresh.
        oldEditorState = EditorState.push(oldEditorState, oldContentState);
        const oldDecorator = oldEditorState.getDecorator();

        oldEditorState = EditorState.set(oldEditorState, { decorator: null });
        oldEditorState = EditorState.set(oldEditorState, {
          decorator: oldDecorator,
        });
        oldEditorState = EditorState.forceSelection(
          oldEditorState,
          oldEditorState.getSelection()
        );

        this.setState({ editorState: oldEditorState });
      }
    };

    const OpenModal =
      currentModal != null &&
      this.modals[currentModal] != null &&
      this.modals[currentModal];

    return (
      <div className="transcript-container">
        {message != null && this.renderMessage()}
        {OpenModal && <OpenModal />}
        <div className="transcript-body">
          <div className="transcript-body-blurb-container">
            <div className="transcript-body-blurb-body">
              Listen to the audio and correct the {secondPass ? "" : "auto-"}
              transcription below so it accurately reflects the call.
            </div>
          </div>
          <TranscriptEditor
            rawContent={this.initialSegments}
            seek={(time, isPlaying) => {
              this.setState({ timestamp: time, manualTimestamp: true }, () =>
                this.setState({ isPlaying: isPlaying })
              );
            }}
            onChange={handleEditorStateChange}
            editorState={this.state.editorState}
            cachedContentState={this.state.cachedContentState}
            hasEvents={transcription.has_events}
            secondPass={secondPass}
            hideEventQualityReasons={this.state.hideEventQualityReasons}
            timestamp={timestamp}
            isPlaying={isPlaying}
          />
        </div>
        <div className="transcript-control" style={currentControlStyle}>
          <div className="transcript-control-instructions">
            <div className="transcript-control-instructions-title">
              Instructions:
            </div>
            <div className="transcript-control-instructions-text">
              Press
              <svg
                className="transcript-control-instructions-enter"
                width="46"
                height="17"
                viewBox="0 0 46 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5.864 13V5.2H10.904V6.928H7.928V8.152H10.472V9.88H7.928V11.272H11.024V13H5.864ZM12.532 13V5.2H14.644L16.372 8.68L17.116 10.432H17.164C17.14 10.224 17.112 9.996 17.08 9.748C17.048 9.5 17.02 9.248 16.996 8.992C16.972 8.728 16.948 8.468 16.924 8.212C16.908 7.948 16.9 7.696 16.9 7.456V5.2H18.868V13H16.756L15.028 9.508L14.284 7.768H14.236C14.284 8.216 14.34 8.704 14.404 9.232C14.468 9.76 14.5 10.264 14.5 10.744V13H12.532ZM22.0825 13V6.928H20.0185V5.2H26.2105V6.928H24.1465V13H22.0825ZM27.3679 13V5.2H32.4079V6.928H29.4319V8.152H31.9759V9.88H29.4319V11.272H32.5279V13H27.3679ZM34.0359 13V5.2H36.9879C37.4039 5.2 37.7999 5.24 38.1759 5.32C38.5599 5.4 38.8919 5.536 39.1719 5.728C39.4599 5.92 39.6879 6.18 39.8559 6.508C40.0239 6.828 40.1079 7.232 40.1079 7.72C40.1079 8.28 39.9919 8.744 39.7599 9.112C39.5359 9.48 39.2319 9.768 38.8479 9.976L40.5399 13H38.2359L36.8919 10.384H36.0999V13H34.0359ZM36.0999 8.752H36.8439C37.6759 8.752 38.0919 8.408 38.0919 7.72C38.0919 7.384 37.9839 7.152 37.7679 7.024C37.5599 6.896 37.2519 6.832 36.8439 6.832H36.0999V8.752Z"
                  fill="#112650"
                />
                <rect
                  x="0.5"
                  y="0.5"
                  width="45"
                  height="16"
                  rx="1.5"
                  stroke="#112650"
                />
              </svg>
              when the speaker changes and make sure to correctly label the
              statement as{" "}
              <span className="transcript-control-instructions-rep">REP</span>{" "}
              or{" "}
              <span className="transcript-control-instructions-cust">CUST</span>
            </div>
            <div className="transcript-control-instructions-text">
              You can also click the speaker labels to switch between them
            </div>
            <Button
              className="transcript-button transcript-control-instructions-button"
              onClick={() => this.openModal("faq")}
              text={"See FAQ"}
            />
          </div>
          <div className="transcript-control-company">
            <div className="transcript-control-company-title">
              COMPANY & REP
            </div>
            <div className="transcript-control-company-company">
              {transcription.company_name}
            </div>
            <div className="transcript-control-company-rep">
              {transcription.rep_name}
            </div>
          </div>
          <div className="transcript-control-buttons">
            <Button
              className="transcript-button transcript-button-full"
              onClick={() => this.openModal("glossary")}
              text={"Glossary"}
            />
            {!secondPass && (
              <Button
                className="transcript-flag transcript-flag-full"
                onClick={() => this.openModal("flag")}
                text={"Flag Call"}
              />
            )}
          </div>
          {!secondPass && (
            <div
              className="transcript-control-link"
              onClick={() => this.openModal("release")}
            >
              Release Call
            </div>
          )}
          <div
            className="transcript-control-link"
            onClick={() => this.openModal("inactivity")}
          >
            Prevent Inactivity Timeout
          </div>
          {!removedSegments && (
            <div
              className="transcript-control-link"
              onClick={() => this.openModal("remove-snippets")}
            >
              Remove All Snippets
            </div>
          )}
          {
            <div
              className="transcript-control-link"
              onClick={() => this.toggleHideEventQualityReasons()}
            >
              Toggle Edit Reasons Visibility
            </div>
          }
          <div
            className="transcript-control-link"
            onClick={() => this.openModal("help")}
          >
            Need help?
          </div>
          <div className="transcript-control-divider" />
          <div className="transcript-control-submit">
            <Button
              className="transcript-submit transcript-submit-full"
              onClick={() => {
                if (
                  segments.length < 2 ||
                  segments[segments.length - 1].content.length >
                    transcription.max_last_segment_length
                ) {
                  this.openModal("low-quality");
                } else {
                  this.openModal("submit");
                }
              }}
              text="Submit Transcript"
            />
          </div>
        </div>
        <div className="transcript-footer">
          <div className="transcript-footer-scrubber">
            <AudioScrubber
              current={timestamp}
              maximum={transcription.call_length_sec}
              updateCurrent={(value) =>
                this.setState({ timestamp: value, manualTimestamp: true })
              }
            />
          </div>
          <div className="transcript-footer-skips">
            <Button
              className="transcript-button"
              onClick={() =>
                this.setState({ timestamp: 0.0, manualTimestamp: true })
              }
            >
              <div className="transcript-button-icon">
                <svg
                  width="16"
                  height="15"
                  viewBox="0 0 16 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.5 8.36602C1.83333 7.98112 1.83333 7.01887 2.5 6.63397L10.75 1.87083C11.4167 1.48593 12.25 1.96706 12.25 2.73686L12.25 12.2631C12.25 13.0329 11.4167 13.5141 10.75 13.1292L2.5 8.36602Z"
                    fill="#112650"
                  />
                  <rect y="2" width="2" height="12" rx="1" fill="#112650" />
                </svg>
              </div>
              <div className="transcript-button-text">Beg</div>
            </Button>
            <Button
              className="transcript-button transcript-button-gap"
              onClick={() =>
                this.setState({
                  timestamp: Math.max(0.0, timestamp - 5.0),
                  manualTimestamp: true,
                })
              }
            >
              <div className="transcript-button-icon">
                <svg
                  width="26"
                  height="15"
                  viewBox="0 0 26 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 8.36602C11.3333 7.98112 11.3333 7.01887 12 6.63397L20.25 1.87083C20.9167 1.48593 21.75 1.96706 21.75 2.73686L21.75 12.2631C21.75 13.0329 20.9167 13.5141 20.25 13.1292L12 8.36602Z"
                    fill="#112650"
                  />
                  <path
                    d="M1.5 8.36602C0.833332 7.98112 0.833333 7.01887 1.5 6.63397L9.75 1.87083C10.4167 1.48593 11.25 1.96706 11.25 2.73686L11.25 12.2631C11.25 13.0329 10.4167 13.5141 9.75 13.1292L1.5 8.36602Z"
                    fill="#112650"
                  />
                </svg>
              </div>
              <div className="transcript-button-text">Rew -5</div>
            </Button>
            <Button
              className="transcript-button-gap transcript-dark-button transcript-dark-button-wide"
              onClick={() => this.setState({ isPlaying: !isPlaying })}
            >
              <div className="transcript-footer-skips-play-icon">
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {isPlaying ? (
                    <React.Fragment>
                      <rect width="5" height="14" rx="1" fill="white" />
                      <rect x="8" width="5" height="14" rx="1" fill="white" />
                    </React.Fragment>
                  ) : (
                    <path
                      d="M13.5 7.13397C14.1667 7.51887 14.1667 8.48112 13.5 8.86602L1.5 15.7942C0.833333 16.1791 -7.73604e-07 15.698 -7.39955e-07 14.9282L-1.34273e-07 1.0718C-1.00623e-07 0.301996 0.833333 -0.17913 1.5 0.20577L13.5 7.13397Z"
                      fill="white"
                    />
                  )}
                </svg>
              </div>
              <div className="transcript-footer-skips-play-text">
                {isPlaying ? "Pause" : "Play"}
              </div>
            </Button>
            <Button
              className="transcript-button transcript-button-gap"
              onClick={() =>
                this.setState({
                  timestamp: Math.min(
                    transcription.call_length_sec,
                    timestamp + 5.0
                  ),
                  manualTimestamp: true,
                })
              }
            >
              <div className="transcript-button-icon">
                <svg
                  width="26"
                  height="15"
                  viewBox="0 0 26 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  transform="rotate(180)"
                >
                  <path
                    d="M12 8.36602C11.3333 7.98112 11.3333 7.01887 12 6.63397L20.25 1.87083C20.9167 1.48593 21.75 1.96706 21.75 2.73686L21.75 12.2631C21.75 13.0329 20.9167 13.5141 20.25 13.1292L12 8.36602Z"
                    fill="#112650"
                  />
                  <path
                    d="M1.5 8.36602C0.833332 7.98112 0.833333 7.01887 1.5 6.63397L9.75 1.87083C10.4167 1.48593 11.25 1.96706 11.25 2.73686L11.25 12.2631C11.25 13.0329 10.4167 13.5141 9.75 13.1292L1.5 8.36602Z"
                    fill="#112650"
                  />
                </svg>
              </div>
              <div className="transcript-button-text">Fwd +5</div>
            </Button>
          </div>
          <div className="transcript-footer-speed">
            <div className="transcript-footer-speed-title">PLAYBACK SPEED</div>
            <Scrubber
              className="transcript-footer-speed-scrubber"
              min={0.0}
              max={1.0}
              value={audioSpeedToUserSpeed(playbackSpeed)}
              onScrubEnd={(value) =>
                this.setState({ playbackSpeed: userSpeedToAudioSpeed(value) })
              }
              onScrubChange={(value) =>
                this.setState({ playbackSpeed: userSpeedToAudioSpeed(value) })
              }
            />
            <div className="transcript-footer-speed-marks">
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark" />
              <div className="transcript-footer-speed-mark transcript-footer-speed-mark-end" />
            </div>
            <div className="transcript-footer-speed-labels">
              <span className="transcript-footer-speed-labels-slower">
                Slower
              </span>
              <span className="transcript-footer-speed-labels-normal">
                Normal
              </span>
              <span className="transcript-footer-speed-labels-faster">
                Faster
              </span>
            </div>
          </div>
          <div className="transcript-footer-button transcript-footer-button-radios">
            <RadioGroup
              selectedValue={playMode}
              onChange={(e) => {
                this.setState({
                  playMode: removedSegments ? "continuous" : e.target.value,
                });
              }}
            >
              <Radio label="Play Snippet" value="snippet" />
              <Radio label="Play Continuous" value="continuous" />
            </RadioGroup>
          </div>
          <div
            className="transcript-footer-button transcript-footer-button-clickable"
            onClick={() => this.setState({ isLooping: !isLooping })}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0)">
                <path
                  d="M0.75 3V7.5H5.25"
                  stroke="#576984"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M17.25 15V10.5H12.75"
                  stroke="#576984"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.3675 6.75C14.9871 5.67508 14.3407 4.71405 13.4884 3.95656C12.6361 3.19907 11.6059 2.66982 10.4938 2.41819C9.38167 2.16656 8.22393 2.20075 7.12861 2.51758C6.03328 2.8344 5.03606 3.42353 4.23 4.23L0.75 7.5M17.25 10.5L13.77 13.77C12.9639 14.5765 11.9667 15.1656 10.8714 15.4824C9.77607 15.7992 8.61833 15.8334 7.50621 15.5818C6.3941 15.3302 5.36385 14.8009 4.5116 14.0434C3.65935 13.2859 3.01288 12.3249 2.6325 11.25"
                  stroke="#576984"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0">
                  <rect width="18" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div className="transcript-footer-button-text">
              Loop:{" "}
              {isLooping ? (
                <span className="transcript-footer-button-text-on">On</span>
              ) : (
                <span className="transcript-footer-button-text-off">Off</span>
              )}
            </div>
          </div>
          <div
            className="transcript-footer-button transcript-footer-button-shortcuts transcript-footer-button-clickable"
            onClick={() => this.openModal("shortcuts")}
          >
            <div className="transcript-footer-button-text">See Shortcuts</div>
            <svg
              width="14"
              height="8"
              viewBox="0 0 14 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 7L7 1L1 7"
                stroke="#576984"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>
    );
  }
}

export default Transcript;
